import {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {DETAILS_VIEW_TYPE, LOADING_STATUS} from "utils/constants";
import {launchDarkly} from "utils/launchDarkly";
import type {DocumentEditorPanelProps} from "types/editor";
import DocumentEditor from "./DocumentEditor.component";
import BnxtDocumentEditor from "./bnxt/BnxtDocumentEditor.component";
import {BNXT_APPLICATION_NAME} from "./bnxt/bnxtOpenTask";
import {EditorModuleMenu} from "./EditorModuleMenu.component";
import "../../style/editor.css";

const TITLE_ID = "external-editor-module-title";

/**
 * The external editor module of the redesigned task details page: the editor the sending system
 * supplies, titled after it ("External editor provided by …"), with the module menu that detaches it into
 * its own window. Business NXT is opened over postMessage while fe-bnxt-open-task-message
 * is on, falling back to the url-based editor once that handshake fails.
 * It replaces taskdetails-remake/panels/editor/DocumentEditorPanel.component on the redesigned page.
 */
export const DocumentEditorPanel = (props: DocumentEditorPanelProps) => {
    const {task, taskDetails, type, params, translate, translationKeyExists, renderInPortal = false} = props;
    const navigate = useNavigate();
    const [bnxtHandshakeFailed, setBnxtHandshakeFailed] = useState(false);
    const onBnxtHandshakeFailed = useCallback(() => setBnxtHandshakeFailed(true), []);

    // a failure says nothing about the next task, so the postMessage editor gets another go
    useEffect(() => setBnxtHandshakeFailed(false), [params.taskId]);

    const getEditorExternalApplicationName = () => {
        switch (type) {
            case DETAILS_VIEW_TYPE.HISTORY_TASK:
            case DETAILS_VIEW_TYPE.PROCESS:
            case DETAILS_VIEW_TYPE.DOCUMENT:
                return task.applicationTypeName;
            default:
                return task.externalApplicationName;
        }
    };

    const getEditorExternalId = () => {
        switch (type) {
            case DETAILS_VIEW_TYPE.HISTORY_TASK:
            case DETAILS_VIEW_TYPE.PROCESS:
                return task.externalApplicationId;
            case DETAILS_VIEW_TYPE.DOCUMENT:
                return task.claimId;
            default:
                return task.externalId;
        }
    };

    if (!taskDetails.externalEditorAvailable) {
        return null;
    }

    const applicationName = task.externalApplicationName || task.applicationTypeName || "";
    const applicationNameKey = `taskDetail.applicationName.${applicationName.replace(/[ .]/g, "")}`;
    const providerName = translationKeyExists(applicationNameKey) ? translate(applicationNameKey) : applicationName;
    const isCompelloTask = !task.taskActions?.includes("postpone");
    const useBnxtOpenTaskMessage = getEditorExternalApplicationName() === BNXT_APPLICATION_NAME
        && launchDarkly.userClient?.variation("fe-bnxt-open-task-message", false)
        && !bnxtHandshakeFailed;

    const editorProps = {
        translate,
        taskId: params.taskId,
        uid: params.uid,
        // left as they are rather than defaulted: the edit request leaves out what is undefined
        id: taskDetails.id,
        isCompelloTask,
        isHistory: props.isHistory,
        isReadOnly: props.editorReadOnly || taskDetails.readonly,
        externalAppId: getEditorExternalId(),
        externalApplicationName: getEditorExternalApplicationName(),
        documentType: task.documentType,
        companyId: task.companyId,
        reapproveType: taskDetails.reapproveType,
        route: navigate,
        viewType: type,
        isPopup: false,
    };

    return (
        <section className={`external-editor${renderInPortal ? " external-editor--side" : ""}`}
                 data-cy="taskDetail.externalEditorPanel"
                 aria-labelledby={TITLE_ID}>
            <header className="external-editor__header">
                <h2 id={TITLE_ID} className="external-editor__title">
                    {`${translate("taskDetail.externalEditor")} ${providerName}`.trim()}
                </h2>
                {!props.inDetachedWindow &&
                    <EditorModuleMenu dataCy="taskDetail.externalEditorPanel" translate={translate}/>}
            </header>
            <div className="external-editor__body">
                {useBnxtOpenTaskMessage ?
                    // the details keep the previous task while the next one loads, and its id would go out in a second openTask
                    <BnxtDocumentEditor {...editorProps}
                                        onHandshakeFailed={onBnxtHandshakeFailed}
                                        detailsLoaded={props.loadingStatus !== LOADING_STATUS.LOADING}/> :
                    <DocumentEditor {...editorProps}/>}
            </div>
        </section>
    );
};

export default DocumentEditorPanel;
